import {
  Alert,
  Box,
  Button,
  Flex,
  Grid,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import type { Error } from 'BrokerTsLib/src/dtos'
import { ContractAcceptationType, Insurer, Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import React, { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { CustomWaypoint } from '@/components/CustomWaypoint'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { Recap } from '@/components/Recap'
import { RecapExtraInsurance } from '@/components/Recap/RecapExtraInsurance'
import { RecapQuote } from '@/components/Recap/RecapQuote'
import {
  UnsavedFormModal
} from '@/components/UnsavedFormModal'
import { FormSection } from '@/containers/forms/FormSection'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useMeetingRecord } from '@/context/MeetingRecordProvider'
import { usePartner } from '@/context/PartnerContext'
import { useSelectedQuote } from '@/context/SelectedQuoteContext'
import { useSidebar } from '@/context/SidebarContext'
import type { CommonMeetingRecord } from '@/dtos/CommonMeetingRecord'
import { useApp } from '@/hooks/useApp'
import { useGetAllDocuments } from '@/hooks/useDocuments'
import { useGetAvailableAddonOffers } from '@/hooks/useOfferAddon'
import { useCreateContract } from '@/hooks/useWizard'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { OfferInvalidMessage } from '@/components/OfferInvalidMessage'
import { Apps } from '@/dtos'
import { useGetOfferValid } from '@/hooks/useOffer'
import { AllianzMeetingRecordForm } from './forms/AllianzMeetingRecordForm'
import CommonMeetingRecordForm from './forms/CommonMeetingRecordForm'
import { CreateContractForm } from './forms/CreateContractForm'
import EssoxMeetingRecordForm from './forms/EssoxMeetingRecordForm'
import KapitolCreateContractForm from './forms/KapitolCreateContractForm'
import KapitolMeetingRecordForm from './forms/KapitolMeetingRecordForm'
import { RespectCreateContractForm } from './forms/RespectCreateContractForm'
import RespectMeetingRecordForm from './forms/RespectMeetingRecordForm'

export type Step4FormValues = {
  meetingRecord: CommonMeetingRecord
}

export const Step4 = () => {
  const { product, app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas
  const { currentPartner } = usePartner()
  const { pageSections, setPageSections } = useSidebar()
  const [acceptChangedPrice, setAcceptChangedPrice] = useState(false)
  const [priceChanged, setPriceChanged] = useState(false)
  const [returnBack, setReturnBack] = useState(true)
  const [alerts, setAlerts] = useState<Error[]>([])
  const [shouldSubmit, setShouldSubmit] = useState(false)

  const {
    isOpen: isJapConfirmOpen,
    onClose: onJapConfirmClose,
    onOpen: onJapConfirmOpen,
  } = useDisclosure()
  const navigate = useNavigate()

  const {
    insuranceParamsId,
    refetchInsuranceParams,
    insuranceParams,
    isDisabled,
  } = useInsuranceParams()
  const { offer, refetch: refetchOffer } = useSelectedQuote()

  const {
    refetch: refetchMR,
    meetingRecord,
    save: saveMeetingRecord,
    isSaving: isSavingMeetingRecord,
  } = useMeetingRecord()

  const {
    mutateAsync: getAvailableAddonOffers,
    data: dataAvailableAddonOffers,
  } = useGetAvailableAddonOffers()
  const availableAddonOffers = dataAvailableAddonOffers?.data

  const { refetch: refetchDocuments } = useGetAllDocuments(
    insuranceParamsId,
    insuranceParams!.generalParams.selectedOfferId!,
    { enabled: false },
  )
  const toast = useToast()

  useEffect(() => {
    if (offer?.offerId)
      getAvailableAddonOffers({
        offerId: offer?.offerId,
        paramsId: insuranceParamsId!,
      })
  }, [offer?.offerId])

  // sjednani smlouvy
  const { mutateAsync: createContract } = useCreateContract()

  const methods = useForm<Step4FormValues>({
    defaultValues: { meetingRecord },
    resolver: yupResolver(yup.object().shape({})) as any, // aby se pripadne resetovaly serverove validace
    mode: isPublicPoleas ? 'onChange' : 'onBlur',
  })

  const {
    handleSubmit,
    reset,
    setError,
    getValues,
    formState: { errors, isSubmitting },
  } = methods

  useEffect(() => {
    if (meetingRecord) reset({ meetingRecord })
  }, [!meetingRecord])

  // zobrazeni validace nabidky ve validation summary (zobrazuje se uz pri getu)
  useEffect(() => {
    if (meetingRecord?.errors)
      meetingRecord.errors.forEach((x) =>
        setError('' as any, { message: x.message }),
      )
  }, [meetingRecord?.errors])

  const isValid = !Object.keys(errors).length
  console.log('isValid', errors, isValid);

  let japConfirmBody = undefined
  if (insuranceParams?.policyHolder.person.email) {
    japConfirmBody = (
      <>
        <Text>
          Návrh smlouvy bude odeslán na email{' '}
          <strong>{insuranceParams?.policyHolder.person.email}</strong>
        </Text>
        <Text>Ověřte prosím správnost emailu před sjednáním.</Text>
      </>
    )
  }

  // handler na click sjednat pojisteni
  const handleSubmitClick = () => {
    setAcceptChangedPrice(priceChanged)
    const contractAcceptationType = getValues('meetingRecord.startType')
    if (
      currentPartner?.partner == 'Jap' &&
      contractAcceptationType == ContractAcceptationType.Payment
    ) {
      onJapConfirmOpen()
    } else {
      setShouldSubmit(true)
    }
  }

  // trigger submitu formulare
  useEffect(() => {
    if (shouldSubmit) {
      setShouldSubmit(false)
      submitForm()
    }
  }, [shouldSubmit])

  async function refetchAfterContractCreate() {
    // zřetězení refetch metod - možná má vliv na omezení výskytu chyby 'Cannot resolve 'register' v MeetingRecordForm nebo CreateContractForm'
    window.scrollTo(0, 0)
    await refetchInsuranceParams()
    await refetchMR()
    await refetchOffer()
  }

  const save = () => {
    const values = getValues()
    return saveMeetingRecord(values.meetingRecord)
      .then((meetingRecord) => {
        toast({
          status: 'success',
          title: 'Nabídka byla uložena.',
        })
        reset({ meetingRecord })
      })
      .catch((err) => {
        if (err.response && err.response.status === 400 && err.response.data) {
          // Přidání prefixu meetingRecord, protože je použitý ve formuláři ale data se postují a vracejí bey něj
          Object.keys(err.response.data).forEach((x) => {
            err.response.data['meetingRecord.' + x] = err.response.data[x]
            delete err.response.data[x]
          })

          const data = err.response.data

          // zobrazeni serverove validace
          Object.keys(data).forEach((x: any) => {
            setError(global.toCamelCase(x), { message: data[x].join(' ') })
          })

          scrollToValidationSummary()
        } else if (err.response && err.response.status === 500) {
          toast({
            status: 'error',
            title: 'Nabídku se nepodařilo uložit.',
          })
        }

        return err
      })
  }

  const saveAndCreateContract = (values: Step4FormValues) => {
    return new Promise((resolve, reject) =>
      saveMeetingRecord(values.meetingRecord)
        .then(() => {
          insuranceParamsFormPersistance.clear()

          // sjednani pojistky
          createContract({ paramsId: insuranceParamsId!, acceptChangedPrice })
            .then((createContractResponse) => {
              if (
                createContractResponse.data.priceChanged &&
                !createContractResponse.data.contractCreated
              ) {
                setPriceChanged(true)
                setReturnBack(createContractResponse.data.returnBack)
                if (createContractResponse.data.errors.length > 0) {
                  setAlerts(createContractResponse.data.errors)
                  scrollToValidationSummary()
                  reject(createContractResponse.data.errors)
                }
              } else if (createContractResponse.data.errors.length > 0) {
                setAlerts(createContractResponse.data.errors)
                scrollToValidationSummary()
                reject([])
              } else {
                refetchAfterContractCreate()
                refetchDocuments()

                resolve(createContractResponse)
              }
            })
            .catch((err) => {
              reject(err)
            })
        })
        .catch((err) => {
          if (
            err.response &&
            err.response.status === 400 &&
            err.response.data
          ) {
            // Přidání prefixu meetingRecord, protože je použitý ve formuláři ale data se postují a vracejí bey něj
            Object.keys(err.response.data).forEach((x) => {
              err.response.data['meetingRecord.' + x] = err.response.data[x]
              delete err.response.data[x]
            })
          }

          reject(err)
        })
        .finally(() => { }),
    )
  }

  const validationSummaryRef = useRef<HTMLDivElement>(null)
  // v nevalidniho formulare se nascroluje na validationSummary
  const scrollToValidationSummary = () => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }

  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(Product.VehicleInsurance, 4, insuranceParamsId, methods as any)

  const submitForm = handleSubmit(
    (values) => {
      return saveAndCreateContract(values)
        .then(() => {
          reset(values)

          if (isPublicPoleas)
            navigate('/poleas/complete')
          else
            navigate(`/steps/complete/${insuranceParamsId}`)
        })
        .catch((err) => {
          if (
            !(err.response && err.response.status === 400 && err.response.data)
          )
            return

          const data = err.response.data

          // zobrazeni serverove validace
          Object.keys(data).forEach((x: any) => {
            setError(global.toCamelCase(x), { message: data[x].join(' ') })
          })

          scrollToValidationSummary()
        })
    },
    (err) => {
      scrollToValidationSummary()
    },
  )

  const isFleet = product == Product.FleetInsurance

  const { refetch: checkOfferValid } = useGetOfferValid(insuranceParams!.generalParams.selectedOfferId!)
  const [offerInvalid, setOfferInvalid] = useState(false)
  useEffect(() => {
    if (offer?.offerId) {
      checkOfferValid().then(({ data }) => {
        setOfferInvalid(!data)
      })
    }
  }, [offer?.offerId])

  // registrace sekcí
  useEffect(() => {
    const array: Section[] = []
    let i = 1

    if (!offer)
      return

    if (offerInvalid) {
      array.push({
        id: 'offerInvalid',
        sectionTitle: 'Neplatná nabídka',
        index: i,
        children: (
          <CustomWaypoint sectionId="offerInvalid">
            <OfferInvalidMessage url={isPublicPoleas ? '/poleas/wizard/2' : `/steps/2/${insuranceParamsId}`} />
          </CustomWaypoint>
        )
      })
    }

    // sekce Rekapitulace pojištění
    array.push({
      id: 'recapitulation',
      sectionTitle: 'Rekapitulace pojištění',
      index: i++,
      children: (
        <CustomWaypoint sectionId="recapitulation">
          <Recap id="recapitulation">
            <RecapQuote />

            {(availableAddonOffers?.length ?? 0) > 0 && (
              <Stack pb={24}>
                {availableAddonOffers?.map(({ insurerId, risks }) => (
                  <RecapExtraInsurance
                    key={insurerId}
                    risks={risks}
                    insurerId={insurerId}
                    offerType={offer!.type}
                  />
                ))}
              </Stack>
            )}
          </Recap>
        </CustomWaypoint>
      ),
    })

    let partnerMeetingRecordForm = null
    let partnerArrangeContractForm = null
    let insurerMeetingRecordForm = null

    if (!isFleet) {
      switch (currentPartner!.partner) {
        case 'Kapitol':
          partnerMeetingRecordForm = <KapitolMeetingRecordForm />
          partnerArrangeContractForm = <KapitolCreateContractForm />
          break
        case 'Essox':
        case 'AutoEsa':
        case 'DirectAuto':
          partnerMeetingRecordForm = <EssoxMeetingRecordForm />
          partnerArrangeContractForm = <CreateContractForm />
          break
        case 'Respect':
          partnerMeetingRecordForm = <RespectMeetingRecordForm />
          partnerArrangeContractForm = <RespectCreateContractForm />
          break
        default:
          partnerMeetingRecordForm = <CommonMeetingRecordForm />
          partnerArrangeContractForm = <CreateContractForm />
          break
      }

      switch (offer?.insurerId) {
        case Insurer.Allianz:
          insurerMeetingRecordForm = <AllianzMeetingRecordForm />
          break
        case Insurer.Kooperativa:
          if (currentPartner!.partner == 'Davocar' ||
            currentPartner!.partner == 'AndyAuto' ||
            currentPartner!.partner == 'TUkas') {
            partnerMeetingRecordForm = <KapitolMeetingRecordForm />
          }
          break
      }
    }

    if (!offerInvalid) {
      if (!isFleet) {
        if (!isPublicPoleas) {
          array.push({
            id: 'meetingRecord',
            sectionTitle: 'Záznam z jednání',
            index: i,
            children: (
              <CustomWaypoint sectionId="recapitulation">
                <PageSection id={'meetingRecord'}>
                  <FormSection
                    number={i++}
                    sectionTitle={'Záznam z jednání'}
                    wide={false}
                  >
                    <Stack gap={12}>
                      {partnerMeetingRecordForm}
                      {insurerMeetingRecordForm}
                    </Stack>
                  </FormSection>
                </PageSection>
              </CustomWaypoint>
            ),
          })
        }

        // sekce Sjednání
        array.push({
          id: 'contract',
          sectionTitle: 'Sjednání',
          index: i,
          useFormSection: true,
          children: (
            <CustomWaypoint sectionId="contract">
              <PageSection id={'contract'}>
                <FormSection number={i++} sectionTitle={'Sjednání'} wide={false}>
                  <Grid rowGap={20}>
                    {!!partnerArrangeContractForm && partnerArrangeContractForm}

                    <Stack gap={8}>
                      <hr></hr>

                      {offer?.underwriting?.required && offer?.underwriting?.message && (
                        <Box sx={{ textAlign: 'center' }}>
                          {offer.underwriting.message}
                        </Box>
                      )}
                      <Flex justifyContent="center" w="100%" gap={4}>
                        <Button
                          loadingText="Ukládám nabídku..."
                          isLoading={isSavingMeetingRecord}
                          onClick={() => save()}
                          variant="solid"
                          colorScheme="primary"
                          isDisabled={isSubmitting || !isValid || isDisabled}
                        >
                          Uložit nabídku
                        </Button>
                        <Button
                          colorScheme="green"
                          isDisabled={isSubmitting || !isValid || isDisabled}
                          isLoading={isSubmitting && isValid}
                          loadingText="Sjednávám..."
                          type="button"
                          variant="solid"
                          onClick={handleSubmitClick}
                        >
                          {!priceChanged
                            ? 'Sjednat pojištění'
                            : 'Sjednat pojištění se změnou ceny'}
                        </Button>
                      </Flex>
                    </Stack>
                  </Grid>
                </FormSection>
              </PageSection>
            </CustomWaypoint>
          ),
        })
      }
    }

    setPageSections(array)

    return () => {
      setPageSections([])
    }
  }, [availableAddonOffers, isSubmitting, isValid, isSavingMeetingRecord, offer, offerInvalid, isPublicPoleas])

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Grid gap={8}>
          {pageSections.map((x) => (
            <React.Fragment key={x.id}>{x.children}</React.Fragment>
          ))}

          <Stack ref={validationSummaryRef}>
            {(priceChanged || alerts.length > 0) && (
              <Alert colorScheme="red" mb={8}>
                <Stack direction="column">
                  {priceChanged && (
                    <Text>
                      Před sjednáním došlo ke změně ceny.
                      {returnBack && (
                        <>
                          {' '}
                          Vraťte se na{' '}
                          <Link
                            as={RouterLink}
                            colorScheme="blue"
                            to={`/steps/2/${insuranceParamsId!}`}
                          >
                            &nbsp;seznam kalkulací&nbsp;
                          </Link>{' '}
                          a vybranou nabídku překalkulujte.
                        </>
                      )}
                    </Text>
                  )}

                  {alerts.length > 0 &&
                    alerts
                      .filter((x) => !!x.message)
                      .map((alert) => (
                        <Text
                          key={alert.message}
                          dangerouslySetInnerHTML={{
                            __html: alert.message ?? '',
                          }}
                        />
                      ))}

                  {!alerts?.length && <Text>
                    Při sjednávání došlo k chybě při komunikaci s pojišťovnou.
                  </Text>}
                </Stack>
              </Alert>
            )}

            <ErrorSummary errors={errors} />
          </Stack>

          <Modal isOpen={isJapConfirmOpen} onClose={onJapConfirmClose} isCentered>
            <ModalOverlay />
            <ModalContent>
              <ModalCloseButton />
              <ModalHeader>Sjednat pojištění</ModalHeader>
              <ModalBody>
                <Box sx={{ paddingBottom: 4 }}>
                  <Text>
                    Skutečně chcete sjednat pojištění k této nabídce a odeslat
                    email klientovi?
                  </Text>
                  {japConfirmBody && japConfirmBody}
                </Box>
                <Flex
                  sx={{
                    width: '100%',
                    alignItems: 'center',
                    gap: 4,
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    colorScheme="green"
                    onClick={() => {
                      onJapConfirmClose()
                      setShouldSubmit(true)
                    }}
                  >
                    Ano
                  </Button>
                  <Button
                    colorScheme="gray"
                    onClick={() => onJapConfirmClose()}
                  >
                    Ne
                  </Button>
                </Flex>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </ModalContent>
          </Modal>

          <UnsavedFormModal {...insuranceParamsFormPersistance} />
        </Grid>
      </form>
    </FormProvider>
  )
}
